import React from 'react';

// SVG structure definitions - single source of truth
const SVG_DEFINITIONS = {
  'hollow-square': {
    viewBox: '0 0 16 16',
    elements: [
      {
        type: 'rect',
        props: { x: 4, y: 4, width: 8, height: 8, fill: 'none' }
      }
    ]
  },
  'diagonal-diamond': {
    viewBox: '0 0 16 16',
    elements: [
      {
        type: 'g',
        props: { transform: 'translate(8,8) rotate(45) translate(-4,-4)' },
        children: [
          {
            type: 'rect',
            props: { x: 0, y: 0, width: 8, height: 8, fill: 'none' }
          },
          {
            type: 'polygon',
            props: { points: '0,8 8,0 8,8' }
          }
        ]
      }
    ]
  }
};

// Helper function to render SVG elements recursively
const renderSvgElement = (element, index, stroke, strokeWidth, fill, fillOpacity) => {
  const { type, props, children } = element;
  const key = `element-${index}`;
  
  // Apply styling based on element type
  const styledProps = { ...props };
  if (props.fill === 'none') {
    styledProps.stroke = stroke;
    styledProps.strokeWidth = strokeWidth;
  } else if (type === 'polygon') {
    styledProps.fill = fill;
    styledProps.opacity = fillOpacity;
  }
  
  if (children) {
    return React.createElement(
      type,
      { key, ...styledProps },
      children.map((child, childIndex) => 
        renderSvgElement(child, `${index}-${childIndex}`, stroke, strokeWidth, fill, fillOpacity)
      )
    );
  }
  
  return React.createElement(type, { key, ...styledProps });
};

// Helper function to convert SVG definition to URL-encoded string
const svgToDataUrl = (iconType) => {
  const svg = SVG_DEFINITIONS[iconType];
  if (!svg) return '';
  
  const renderElementToString = (element) => {
    const { type, props, children } = element;
    const attrs = Object.entries(props)
      .map(([key, value]) => {
        // Handle special cases for URL encoding
        if (key === 'fill' && value === 'none') {
          return `${key}='none' stroke='%23000' stroke-width='1'`;
        } else if (key === 'fill' && type === 'polygon') {
          return `${key}='%23333'`;
        }
        return `${key}='${value}'`;
      })
      .join(' ');
    
    if (children) {
      const childrenString = children.map(renderElementToString).join('');
      return `<${type} ${attrs}>${childrenString}</${type}>`;
    }
    
    return `<${type} ${attrs}/>`;
  };
  
  const elementsString = svg.elements.map(renderElementToString).join('');
  return `%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='${svg.viewBox}'%3E${elementsString}%3C/svg%3E`;
};

// Custom SVG icons for drawing modes that match cursor designs

export const HollowSquareIcon = ({ size = 16, color = 'currentColor', strokeWidth = 1.5 }) => {
  const svg = SVG_DEFINITIONS['hollow-square'];
  return (
    <svg width={size} height={size} viewBox={svg.viewBox}>
      {svg.elements.map((element, index) => 
        renderSvgElement(element, index, color, strokeWidth, color, 0.3)
      )}
    </svg>
  );
};

export const DiagonalDiamondIcon = ({ size = 16, color = 'currentColor', strokeWidth = 1.5, fillOpacity = 0.3 }) => {
  const svg = SVG_DEFINITIONS['diagonal-diamond'];
  return (
    <svg width={size} height={size} viewBox={svg.viewBox}>
      {svg.elements.map((element, index) => 
        renderSvgElement(element, index, color, strokeWidth, color, fillOpacity)
      )}
    </svg>
  );
};

// Utility functions to generate cursor data URLs
export const getCursorDataUrl = (iconType) => {
  const baseUrl = "data:image/svg+xml,";
  const svgString = svgToDataUrl(iconType);
  
  if (!svgString) {
    return 'default';
  }
  
  return `${baseUrl}${svgString}`;
};
